import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import TopNavigation from '../../components/TopNavigation';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import ArticleHero from './components/ArticleHero';
import ArticleContent from './components/ArticleContent';
import ShareActions from './components/ShareActions';
import RelatedPosts from './components/RelatedPosts';
import ReadingProgress from './components/ReadingProgress';
import ImageLightbox from './components/ImageLightbox';
import Button from '../../components/ui/Button';
import { postsService } from '../../services/postsService';

const BlogPostDetail = () => {
  const { slug: slugFromUrl } = useParams();
  const [searchParams] = useSearchParams();
  const slugFromQuery = searchParams?.get('slug');
  const navigate = useNavigate();
  
  // Support both URL params (/blog/:slug) and query params (?slug=...) for backward compatibility
  const slug = slugFromUrl || slugFromQuery;

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    if (slug) {
      loadPost();
    } else {
      setError('No post slug provided');
      setLoading(false);
    }
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const data = await postsService?.getPublishedPostBySlug(slug);
      setPost(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Post not found');
      console.error('Load post error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedPosts = async () => {
    try {
      const allPosts = await postsService?.getPublishedPosts();
      const filtered = allPosts?.filter(p => p?.slug !== slug)?.slice(0, 3);
      setRelatedPosts(filtered);
    } catch (err) {
      console.error('Load related posts error:', err);
    }
  };

  useEffect(() => {
    if (post) {
      loadRelatedPosts();
    }
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopNavigation />
        <div className="content-area">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading post...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <TopNavigation />
        <div className="content-area">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-lg text-muted-foreground mb-4">Post not found.</p>
              <Button onClick={() => navigate('/blog-listing')}>Back to Blog</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const postUrl = `${window.location?.origin}/blog-post-detail?slug=${slug}`;

  const breadcrumbItems = [
    { label: 'Home', path: '/homepage' },
    { label: 'Blog', path: '/blog-listing' },
    { label: post?.title, path: `/blog-post-detail?slug=${slug}` }
  ];

  return (
    <>
      <Helmet>
        <title>{post?.title} | Sleeve Blog</title>
        <meta name="description" content={post?.excerpt} />
        <meta property="og:title" content={post?.title} />
        <meta property="og:description" content={post?.excerpt} />
        <meta property="og:image" content={post?.heroImage} />
        <meta property="og:url" content={postUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <ReadingProgress />
      <TopNavigation />
      <div className="content-area min-h-screen bg-background">
        <div className="max-w-4xl mx-auto py-6 md:py-8 lg:py-10">
          <BreadcrumbNavigation customItems={breadcrumbItems} />

          <div className="mt-6 md:mt-8 lg:mt-10">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/blog-listing')}
              className="mb-6 md:mb-8">

              Back to Blog
            </Button>

            <ArticleHero
              title={post?.title}
              author={post?.authorId}
              publishedAt={post?.createdAt}
              heroImage={post?.heroImage}
              heroImageAlt={post?.heroImageAlt}
              content={post?.content}
            />

            <div className="mt-8 md:mt-10 lg:mt-12">
              <ArticleContent
                content={post?.content}
                embeddedImages={post?.embeddedImages || []}
                onImageClick={setLightboxImage} />

            </div>

            <ShareActions postTitle={post?.title} postUrl={postUrl} />

            <RelatedPosts posts={relatedPosts} />
          </div>
        </div>
      </div>
      {lightboxImage &&
      <ImageLightbox
        image={lightboxImage?.url}
        alt={lightboxImage?.alt}
        onClose={() => setLightboxImage(null)} />

      }
    </>);

};

export default BlogPostDetail;